package com.patronat.top.client.presentation.home

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import com.patronat.top.client.presentation.home.components.HomeCategorySection
import com.patronat.top.client.presentation.home.components.HomeSectionHeader
import com.patronat.top.client.presentation.home.components.HomeVenueEmptyState
import com.patronat.top.client.presentation.home.components.VenueListCard
import com.patronat.top.ui.atoms.AppSearchField
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyLarge
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White
import org.jetbrains.compose.resources.stringResource
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_home_location_city
import top.clientapp.generated.resources.client_home_search_placeholder

@Composable
fun ClientHomeDesktopContent(
    state: ClientHomeViewState,
    onEvent: (ClientHomeEvent) -> Unit,
    onInstallRequired: () -> Unit,
    modifier: Modifier = Modifier
) {
    val fallbackLocation = stringResource(Res.string.client_home_location_city)
    val locationTitle = state.currentLocationTitle?.takeIf(String::isNotBlank) ?: fallbackLocation

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(White)
            .padding(horizontal = 24.dp),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .widthIn(max = 1392.dp)
                .padding(top = 84.dp, bottom = 24.dp)
        ) {
            DesktopTopBar(
                searchQuery = state.searchQuery,
                locationTitle = locationTitle,
                onSearchChange = { onEvent(ClientHomeEvent.OnSearchChanged(it)) },
                onLocationClick = { onEvent(ClientHomeEvent.OnLocationSelectorClicked) },
                onFilterClick = onInstallRequired,
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(modifier = Modifier.height(16.dp))

            DesktopVenueGrid(
                state = state,
                onEvent = onEvent,
                onInstallRequired = onInstallRequired,
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
            )
        }
    }
}

@Composable
private fun DesktopTopBar(
    searchQuery: String,
    locationTitle: String,
    onSearchChange: (String) -> Unit,
    onLocationClick: () -> Unit,
    onFilterClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        AppSearchField(
            value = searchQuery,
            onValueChange = onSearchChange,
            placeholder = stringResource(Res.string.client_home_search_placeholder),
            modifier = Modifier.width(382.dp)
        )

        Row(
            horizontalArrangement = Arrangement.spacedBy(22.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                modifier = Modifier.clickable(onClick = onLocationClick),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    painter = AppIcons.VenueLocation,
                    contentDescription = null,
                    tint = MineShaft950,
                    modifier = Modifier.size(22.dp)
                )

                Text(
                    text = locationTitle,
                    style = BodyLarge.copy(color = MineShaft950)
                )

                Icon(
                    painter = AppIcons.ArrowDown,
                    contentDescription = null,
                    tint = MineShaft950,
                    modifier = Modifier.size(16.dp)
                )
            }

            Box(
                modifier = Modifier
                    .size(44.dp)
                    .clip(RoundedCornerShape(10.dp))
                    .background(MineShaft50)
                    .clickable(onClick = onFilterClick),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = AppIcons.FilterSliders,
                    contentDescription = null,
                    tint = MineShaft950,
                    modifier = Modifier.size(22.dp)
                )
            }
        }
    }
}

@Composable
private fun DesktopVenueGrid(
    state: ClientHomeViewState,
    onEvent: (ClientHomeEvent) -> Unit,
    onInstallRequired: () -> Unit,
    modifier: Modifier = Modifier
) {
    val panelShape = RoundedCornerShape(10.dp)

    LazyVerticalGrid(
        columns = GridCells.Adaptive(minSize = 390.dp),
        modifier = modifier
            .clip(panelShape)
            .border(1.dp, MineShaft100, panelShape),
        contentPadding = PaddingValues(start = 24.dp, top = 28.dp, end = 24.dp, bottom = 24.dp),
        horizontalArrangement = Arrangement.spacedBy(22.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp)
    ) {
        item(span = { GridItemSpan(maxLineSpan) }) {
            HomeCategorySection(
                selectedGroup = state.selectedGroup,
                onCategoryClick = { onEvent(ClientHomeEvent.OnHomeCategoryClicked(it)) },
                onViewAllClick = onInstallRequired,
                modifier = Modifier.fillMaxWidth()
            )
        }

        item(span = { GridItemSpan(maxLineSpan) }) {
            HomeSectionHeader(
                title = ClientHomeVenuesTitle,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 4.dp)
            )
        }

        if (state.isLoading && state.venues.isEmpty()) {
            item(span = { GridItemSpan(maxLineSpan) }) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(180.dp),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = Primary600)
                }
            }
        } else if (state.venues.isEmpty()) {
            item(span = { GridItemSpan(maxLineSpan) }) {
                HomeVenueEmptyState(modifier = Modifier.fillMaxWidth())
            }
        } else {
            items(
                items = state.venues,
                key = { it.id }
            ) { venue ->
                VenueListCard(
                    venue = venue,
                    isFavorite = venue.id in state.favoriteVenueIds,
                    onClick = { onEvent(ClientHomeEvent.OnVenueClicked(venue.id)) },
                    onFavoriteClick = onInstallRequired,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

private const val ClientHomeVenuesTitle = "Заклади"
